import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { profileService, currenciesService } from '../../api/apiClient';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Select } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { Badge } from '../../components/UI/Badge';
import {
  User,
  Lock,
  AlertTriangle,
  Save,
  Trash2,
} from 'lucide-react';

export const Profile = () => {
  const { user, refreshUser, logout } = useAuth();
  const { success, error: toastError } = useToast();

  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Profile Form
  const [profileData, setProfileData] = useState({
    locale: user?.locale || 'es',
    default_currency_id: user?.default_currency_id ? String(user.default_currency_id) : '',
  });

  // Password Form
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete Account
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const res = await currenciesService.getAll();
        if (res && res.data) {
          setCurrencies(res.data);
        }
      } catch (err) {
        console.error('Error loading currencies:', err);
      }
    };
    loadCurrencies();
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      if (profileData.locale) formData.append('locale', profileData.locale);
      if (profileData.default_currency_id) formData.append('default_currency_id', profileData.default_currency_id);

      await profileService.update(formData);
      await refreshUser();
      success('Perfil actualizado correctamente.');
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al actualizar perfil.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toastError('Las contraseñas no coinciden.');
      return;
    }

    setPasswordLoading(true);
    try {
      await profileService.changePassword(passwordData);
      success('Contraseña cambiada exitosamente.');
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al cambiar contraseña.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteLoading(true);
    try {
      const res = await profileService.deleteAccount({ password: deletePassword });
      success(res.message || 'Tu cuenta ha sido desactivada.');
      setDeleteModalOpen(false);
      await logout();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al procesar la eliminación.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
          <User className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
          Mi Perfil & Ajustes de Cuenta
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
          Administra tus preferencias de facturación, seguridad y configuración general
        </p>
      </div>

      {/* Profile Overview Card */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#08707A] text-white flex items-center justify-center text-xl font-bold shadow-md">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">
                  {user?.name}
                </h2>
                {user?.is_admin && <Badge variant="admin" size="sm">ADMIN</Badge>}
                {user?.is_subscribed && <Badge variant="success" size="sm">PRO</Badge>}
              </div>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Preferences Form */}
      <Card>
        <CardHeader>
          <CardTitle subtitle="Configura el idioma y la moneda por defecto en tus comprobantes">
            Preferencias Regionales
          </CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Idioma de la Plataforma"
                value={profileData.locale}
                onChange={(e) => setProfileData(prev => ({ ...prev, locale: e.target.value }))}
              >
                <option value="es">Español (ES)</option>
                <option value="en">English (US)</option>
              </Select>

              <Select
                label="Moneda Predeterminada"
                value={profileData.default_currency_id}
                onChange={(e) => setProfileData(prev => ({ ...prev, default_currency_id: e.target.value }))}
              >
                <option value="">-- Sin Moneda Específica --</option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} ({c.symbol}) - {c.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" variant="primary" size="sm" icon={Save} isLoading={loading} className="bg-[#08707A] hover:bg-[#065B63]">
                Guardar Preferencias
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Password Change Card */}
      <Card>
        <CardHeader>
          <CardTitle subtitle="Actualiza tu contraseña de acceso">
            Seguridad de la Cuenta
          </CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Nueva Contraseña (mínimo 8 caracteres)"
                type="password"
                placeholder="••••••••"
                icon={Lock}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                required
              />

              <Input
                label="Confirmar Nueva Contraseña"
                type="password"
                placeholder="••••••••"
                icon={Lock}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                required
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" variant="primary" size="sm" icon={Save} isLoading={passwordLoading} className="bg-[#08707A] hover:bg-[#065B63]">
                Actualizar Contraseña
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 dark:border-red-900/60 bg-red-50/30 dark:bg-red-950/20">
        <CardHeader className="border-red-100 dark:border-red-900/40">
          <CardTitle subtitle="Acciones irreversibles sobre tus datos">
            <span className="text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Zona de Peligro
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
              Desactivar y Eliminar Cuenta
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-lg leading-relaxed">
              Al desactivar tu cuenta, se cancelará tu suscripción y dispondrás de un plazo de 30 días para volver a iniciar sesión antes de su eliminación permanente.
            </p>
          </div>

          <Button
            variant="danger"
            size="sm"
            icon={Trash2}
            onClick={() => setDeleteModalOpen(true)}
          >
            Eliminar Mi Cuenta
          </Button>
        </CardBody>
      </Card>

      {/* Delete Account Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirmar Desactivación de Cuenta"
        subtitle="Por favor ingresa tu contraseña para confirmar esta acción"
      >
        <form onSubmit={handleDeleteAccount} className="p-6 space-y-4">
          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/60 border border-red-200 dark:border-red-800 text-xs text-red-800 dark:text-red-200 leading-relaxed">
            Tu cuenta entrará en un periodo de gracia de 30 días. Si no inicias sesión durante ese tiempo, tus facturas, cotizaciones e inventario serán eliminados permanentemente.
          </div>

          <Input
            label="Contraseña Actual"
            type="password"
            placeholder="••••••••"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            required
            autoFocus
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              variant="secondary"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleteLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="danger"
              isLoading={deleteLoading}
            >
              Confirmar Desactivación
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
